// src/tabs/messages/segment-editor/variable-labels.js — Lot 1 (éditeur segmenté)
//
// Traduit un NOM de variable brut ({guest_first_name}, {is_airbnb}…) en label
// HUMAIN pour l'affichage en pastille. Source = listAvailableVariables() (le
// catalogue déjà utilisé par le picker). Fallback = le nom brut (jamais un
// affichage vide → une variable hors catalogue reste identifiable). PUR, 0 I/O.
//
// Le stockage ne change PAS : la pastille porte `name` (le token), on n'affiche
// que son label. serializeSegments réécrit `{name}` à l'octet près.

import { listAvailableVariables } from "../../../lib/template-variables";
import { parseIfExpression } from "../../../lib/message-segments";

// ── Libellés humains courts (chantier A Lot 2 — table VALIDÉE Dimitri) ────
// Arbitrages confirmés : (1) collisions résolues par « … du voyageur » vs
// « … conciergerie » ; (2) alias de dates = MÊME libellé que leur cible
// (date_arrivee ≡ checkin_date → « Date d'arrivée », sans suffixe) ; (3) le
// libellé technique long du catalogue est conservé en TOOLTIP (title) du
// picker. Consultée EN PRIORITÉ par variableLabel() → pastilles, encarts
// génériques (ifEncartMeta) et picker (humanPickerLabel). Le catalogue moteur
// src/lib/template-variables.js reste INTACT (ses labels descriptifs servent
// le reste de l'app). Inclut `prenom`/`logement` (reçu, hors catalogue) pour
// qu'AUCUNE variable n'affiche son nom technique brut.
export const HUMAN_LABELS = {
  // 👤 Voyageur
  guest_first_name: "Prénom",
  guest_last_name: "Nom de famille",
  guest_email: "Email du voyageur",
  guest_phone: "Téléphone du voyageur",
  guest_language: "Langue du voyageur",
  guest_count: "Nombre de voyageurs",
  // 📅 Réservation
  checkin_date: "Date d'arrivée",
  checkout_date: "Date de départ",
  nights: "Nombre de nuits",
  ota_source: "Plateforme",
  total_amount: "Montant total",
  cleaning_fee: "Frais de ménage",
  ota_chat_link: "Lien messagerie plateforme",
  // PARTIE B (ADR-272) — variables Direct
  request_ref: "Référence de la demande",
  cancellation_policy: "Politique d'annulation",
  // 🏠 Logement
  logement_name: "Nom du logement",
  logement_full_name: "Nom complet du logement",
  logement_address: "Adresse",
  logement_address_full: "Adresse complète",
  logement_address_precision: "Précisions d'adresse",
  // ADR-272 (message d'arrivée) — ville du logement (source dédiée public_address).
  ville: "Ville",
  logement_floor: "Étage",
  access_building_instructions: "Accès au bâtiment",
  access_apartment_instructions: "Accès au logement",
  wifi_name: "Nom du Wi-Fi",
  wifi_code: "Mot de passe Wi-Fi",
  wifi_instructions: "Instructions Wi-Fi",
  door_code: "Code porte d'entrée",
  box_code: "Code boîte à clés",
  interphone_code: "Code interphone",
  coffre_code: "Code coffre",
  garage_code: "Code garage",
  parking_info: "Parking",
  parking_info_long: "Parking (détails)",
  poubelles_info: "Poubelles",
  infos_poubelles_long: "Poubelles (détails)",
  infos_courses_stockage: "Courses & stockage",
  regles_maison: "Règles de la maison",
  livret_url: "Lien livret d'accueil",
  avis_google_url: "Lien avis Google",
  checkin_time: "Heure d'arrivée",
  checkout_time: "Heure de départ",
  // ADR-272 (message d'arrivée) — ALIAS : MÊME libellé que leur cible
  // (convention des alias, cf. date_arrivee ≡ checkin_date).
  heure_arrivee: "Heure d'arrivée",
  heure_depart: "Heure de départ",
  // 🛎️ Conciergerie
  fbz_name: "Nom de la conciergerie",
  fbz_phone: "Téléphone conciergerie",
  // ADR-272 (message d'arrivée) — ALIAS de fbz_phone (même valeur, même libellé).
  telephone_contact: "Téléphone conciergerie",
  fbz_email: "Email conciergerie",
  fbz_logo_url: "Logo (lien)",
  // 🧩 Plateformes (pastille inline ; l'ENCART garde son libellé PLATFORM_IF)
  is_airbnb: "Vient d'Airbnb",
  is_booking: "Vient de Booking",
  is_direct: "Réservation en direct",
  // 🧮 Computed
  caution_required: "Caution requise",
  // rating_phrase : DEPRECATED au picker (POINT 2 — convertie en encarts
  // {#if is_airbnb}/{#if is_booking}) mais le label est CONSERVÉ pour que la
  // pastille d'un vieux brouillon reste lisible.
  rating_phrase: "Phrase d'avis (5 étoiles / 10-10)",
  source: "Source",
  revenue: "Montant",
  date_arrivee: "Date d'arrivée",
  date_depart: "Date de départ",
  welcome_form_url: "Lien formulaire de bienvenue",
  // Lot O1 OTA — lien unique espace voyageur OTA (généré JIT par le moteur)
  lien_sejour: "Lien espace voyageur",
  checkin_form_url: "Lien formulaire de check-in",
  deposit_url: "Lien de caution",
  payment_received: "Séjour payé",
  caution_received: "Caution reçue",
  deposit_link_visible: "Lien caution affichable",
  welcome_checkin_allowed: "Instructions d'arrivée autorisées",
  // M5 (ADR-267) — variables inverses (blocs « seulement si … NON … »)
  caution_manquante: "Caution non reçue",
  // ADR-174 — mode de caution (reflet du calcul Stripe selon la durée).
  caution_empreinte: "Caution en empreinte (jamais débitée)",
  caution_debit: "Caution en débit réel (prélevée puis remboursée)",
  formulaire_manquant: "Formulaire de bienvenue non rempli",
  formulaire_rempli: "Formulaire de bienvenue rempli",
  caution_status: "Statut de la caution",
  caution_amount: "Montant de la caution",
  caution_link: "Lien paiement caution",
  paiement_caution_url: "Lien caution automatique",
  paiement_sejour_status: "Statut paiement séjour",
  paiement_sejour_link: "Lien paiement séjour",
  caution_authorized: "Caution autorisée (empreinte)",
  caution_captured: "Caution encaissée",
  paid_at_formatted: "Date de paiement",
  // 📄 Reçu de séjour (RECEIPT_VAR_GROUPS, hors catalogue moteur — évitait
  // jusqu'ici un affichage en nom technique brut dans la pastille)
  prenom: "Prénom",
  logement: "Nom du logement",
};

// Cache lazy nom→label (le catalogue est statique). Construit à la 1re demande.
let _labelMap = null;
function labelMap() {
  if (_labelMap) return _labelMap;
  const m = new Map();
  try {
    for (const group of listAvailableVariables() || []) {
      for (const v of group.variables || []) {
        // La 1re occurrence gagne (is_airbnb apparaît en « Bloc conditionnel »
        // ET en « Computed » — on garde le libellé le plus parlant, le 1er).
        if (v && v.key && !m.has(v.key)) m.set(v.key, v.label || v.key);
      }
    }
  } catch (_) {
    // Catalogue indisponible (mock vide en test) → map vide, fallback nom brut.
  }
  _labelMap = m;
  return m;
}

/**
 * Label humain d'une variable : libellé court VALIDÉ (HUMAN_LABELS) en
 * priorité, sinon label long du catalogue, sinon le nom brut (jamais vide —
 * une variable inconnue reste identifiable).
 */
export function variableLabel(name) {
  if (!name) return "";
  return HUMAN_LABELS[name] || labelMap().get(name) || name;
}

/**
 * Libellé d'une entrée du PICKER (« + Info voyageur ») : libellé court validé
 * en priorité, sinon le label du groupe (RECEIPT_VAR_GROUPS ou catalogue),
 * sinon la clé. Le label long reste disponible pour le tooltip (title).
 * @param {{key?:string, label?:string}} v
 */
export function humanPickerLabel(v) {
  if (!v) return "";
  return HUMAN_LABELS[v.key] || v.label || v.key || "";
}

// ── Blocs conditionnels par PLATEFORME (encarts colorés dédiés) ──────────
// Les 3 blocs plateforme ont un rendu propre (rose/bleu/vert) + libellé fort.
// Tout autre {#if var} → encart générique ambre « Si "Label" est renseigné ».
export const PLATFORM_IF = {
  is_airbnb: {
    label: "UNIQUEMENT AIRBNB",
    border: "#ED93B1",
    bg: "#FBEAF0",
    text: "#993556",
  },
  is_booking: {
    label: "UNIQUEMENT BOOKING",
    border: "#85B7EB",
    bg: "#E6F1FB",
    text: "#185FA5",
  },
  is_direct: {
    label: "UNIQUEMENT EN DIRECT",
    border: "#9FE1CB",
    bg: "#E1F5EE",
    text: "#0F6E56",
  },
};

// ── Blocs conditionnels MÉTIER (POINT 4 — liste V1 validée Dimitri) ──────
// Conditions proposées à l'INSERTION dans le menu « + Paragraphe conditionnel »
// (groupe « Conditions »), avec un bandeau explicite dédié — couleur = l'ambre
// générique existant (pas de nouvelle couleur). RIEN d'autre en V1 :
// payment_received est EXCLU (sémantique OTA fausse — « payé » côté plateforme
// ne veut pas dire encaissé FBZ). Tout autre {#if var} tapé à la main garde le
// fallback générique « Si « X » est renseigné » d'ifEncartMeta.
export const CONDITION_IF = {
  caution_required: {
    label: "SEULEMENT SI CAUTION REQUISE",
    border: "#EF9F27",
    bg: "#FAEEDA",
    text: "#854F0B",
  },
  caution_received: {
    label: "SEULEMENT SI CAUTION REÇUE",
    border: "#EF9F27",
    bg: "#FAEEDA",
    text: "#854F0B",
  },
  // M5 (ADR-267) — blocs NÉGATIFS via variables inverses (résolvent non-vide
  // quand la chose MANQUE ; zéro modification du parseur {#if}).
  caution_manquante: {
    label: "SEULEMENT SI CAUTION NON REÇUE",
    border: "#EF9F27",
    bg: "#FAEEDA",
    text: "#854F0B",
  },
  formulaire_manquant: {
    label: "SEULEMENT SI FORMULAIRE NON REMPLI",
    border: "#EF9F27",
    bg: "#FAEEDA",
    text: "#854F0B",
  },
  formulaire_rempli: {
    label: "SEULEMENT SI FORMULAIRE REMPLI",
    border: "#EF9F27",
    bg: "#FAEEDA",
    text: "#854F0B",
  },
};

// Menu du bouton « + Paragraphe conditionnel » — groupe « Plateformes »
// (ordre = Airbnb/Booking). « Direct » est RETIRÉ du menu d'INSERTION
// (décision Dimitri : le Direct a son propre onglet, il n'a rien à faire ici)
// — le RENDU d'un bloc {#if is_direct} déjà présent dans un template reste
// entièrement supporté via PLATFORM_IF ci-dessus (affichage, édition du
// contenu, suppression). Seul l'AJOUT disparaît.
export const PLATFORM_BLOCK_OPTIONS = [
  { name: "is_airbnb", label: "Airbnb" },
  { name: "is_booking", label: "Booking" },
];

// Groupe « Conditions » du même menu (POINT 4 liste V1 caution ; + M5 ADR-267 :
// blocs négatifs « non reçue » / « non rempli » + miroir positif formulaire).
export const CONDITION_BLOCK_OPTIONS = [
  { name: "caution_required", label: "Caution requise" },
  { name: "caution_received", label: "Caution reçue" },
  { name: "caution_manquante", label: "Caution non reçue" },
  { name: "formulaire_manquant", label: "Formulaire non rempli" },
  { name: "formulaire_rempli", label: "Formulaire rempli" },
];

// Groupe « Infos logement (si renseignée) » (décision Dimitri 2026-07-08) :
// blocs {#if variable} GÉNÉRIQUES — le paragraphe ne s'affiche au rendu que si
// la variable logement est non vide (sémantique déjà vivante en prod : 10 de
// ces blocs existent dans les corps seedés ; seule la CRÉATION au menu
// manquait). Rendu/édition/sérialisation = fallback générique existant
// (ifEncartMeta, bandeau ambre) — AUCUN nouveau mécanisme, moteur intouché.
// EXCLUS volontairement (blocs trompeurs car toujours renseignés) : nom /
// adresse courte du logement, heures check-in/out, Wi-Fi nom/code seuls
// (couverts par « instructions Wi-Fi »).
// ⚠️ Nuance (pessimist #943) : `wifi_instructions` a un fallback DÉRIVÉ des
// réseaux Wi-Fi (ADR-198, renseignés quasi partout) et `livret_url` est
// pré-résolue dès qu'un livret est publié → ces 2 blocs seront quasi toujours
// affichés (sémantique « le Wi-Fi / livret existe », pas « note libre saisie »).
// Les 14 autres sont stricts (champ self vide → bloc masqué).
export const LOGEMENT_INFO_BLOCK_OPTIONS = [
  { name: "access_building_instructions", label: "Accès immeuble" },
  { name: "access_apartment_instructions", label: "Accès logement" },
  { name: "wifi_instructions", label: "Instructions Wi-Fi" },
  { name: "parking_info_long", label: "Parking (détaillé)" },
  { name: "infos_poubelles_long", label: "Poubelles (détaillé)" },
  { name: "infos_courses_stockage", label: "Courses / stockage" },
  { name: "regles_maison", label: "Règles maison" },
  { name: "livret_url", label: "Lien livret d'accueil" },
  { name: "logement_address_full", label: "Adresse complète" },
  { name: "logement_address_precision", label: "Précision d'adresse" },
  { name: "door_code", label: "Code porte" },
  { name: "box_code", label: "Code boîte à clés" },
  { name: "interphone_code", label: "Code interphone" },
  { name: "coffre_code", label: "Code coffre" },
  { name: "garage_code", label: "Code garage" },
  { name: "avis_google_url", label: "Lien avis Google" },
];

// Menu GROUPÉ du bouton « + Paragraphe conditionnel » (même pattern d'en-têtes
// de groupe que le picker « + Info voyageur »). L'insertion reste insertBlock().
export const BLOCK_MENU_GROUPS = [
  { category: "Plateformes", options: PLATFORM_BLOCK_OPTIONS },
  { category: "Conditions", options: CONDITION_BLOCK_OPTIONS },
  { category: "Infos logement (si renseignée)", options: LOGEMENT_INFO_BLOCK_OPTIONS },
];

// Palette ambre générique (conditions + cumuls + expression non supportée) —
// AUCUNE couleur neuve n'est introduite pour le cumul (décision Dimitri).
const AMBER = { border: "#EF9F27", bg: "#FAEEDA", text: "#854F0B" };

// Bandeau d'une expression que le MOTEUR ne sait pas évaluer (arité paire,
// mélange ET/OU, opérateur inconnu) : evalIfExpression renvoie false → le bloc
// n'est JAMAIS affiché. On le dit tel quel (règle « texte d'aide = état réel »)
// et on ne « répare » jamais l'expression à la place de l'admin.
export const INVALID_EXPR_LABEL =
  "CONDITION NON RECONNUE — CE PARAGRAPHE NE S'AFFICHERA JAMAIS";

/**
 * Fragment de libellé d'UN opérande, pour composer le bandeau d'un cumul.
 * Réutilise les libellés déjà validés (plateforme / condition métier), sans le
 * préfixe (« SEULEMENT SI … », « UNIQUEMENT … ») qui est porté une seule fois
 * par le bandeau composé.
 */
export function ifOperandPhrase(name) {
  const p = PLATFORM_IF[name];
  if (p) return p.label.replace(/^UNIQUEMENT\s+/, "");
  const c = CONDITION_IF[name];
  if (c) return c.label.replace(/^SEULEMENT SI\s+/, "");
  return String(variableLabel(name)).toUpperCase();
}

/**
 * Métadonnées d'affichage d'un encart {#if EXPR}.
 *
 * • EXPR à UN opérande (cas historique, 99 % des contenus prod) → comportement
 *   STRICTEMENT inchangé : plateforme (couleur dédiée) → condition métier
 *   (bandeau explicite, ambre) → fallback générique.
 * • EXPR de CUMUL (`a ET b`) → bandeau composé ambre, variant 'cumul'.
 * • EXPR non supportée par le moteur → variant 'invalid' (bloc jamais affiché).
 *
 * @param {string} expr nom de variable OU expression de cumul (capture brute)
 * @returns {{variant:'platform'|'condition'|'generic'|'cumul'|'invalid',
 *            label:string, border:string, bg:string, text:string}}
 */
export function ifEncartMeta(expr) {
  const parsed = parseIfExpression(expr);
  if (!parsed.valid) {
    return { variant: "invalid", label: INVALID_EXPR_LABEL, ...AMBER };
  }
  if (parsed.operands.length > 1) {
    const phrase = parsed.operands
      .map(ifOperandPhrase)
      .join(` ${parsed.operator} `);
    return { variant: "cumul", label: `SEULEMENT SI ${phrase}`, ...AMBER };
  }
  const name = parsed.operands[0];
  const p = PLATFORM_IF[name];
  if (p) return { variant: "platform", ...p };
  const c = CONDITION_IF[name];
  if (c) return { variant: "condition", ...c };
  return {
    variant: "generic",
    // Libellé lisible : « Si "Label" est renseigné » (état actuel : un {#if var}
    // n'affiche son contenu que si la variable est non vide au rendu).
    label: `Si « ${variableLabel(name)} » est renseigné`,
    ...AMBER,
  };
}
